import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Camera,
  Receipt,
  Bell,
  Wrench,
  CalendarClock,
  School as SchoolIcon,
  Sparkles,
  X,
  Handshake,
  ArrowRight,
} from "lucide-react";
import FdpInfoDialog from "@/components/FdpInfoDialog";

const FEATURES = [
  { icon: FileText, title: "Orçamentos online", desc: "Aprove e acompanhe tudo pelo celular." },
  { icon: Camera, title: "Fotos antes/depois", desc: "Veja o progresso de cada serviço." },
  { icon: Receipt, title: "Nota fiscal e termos", desc: "Baixe NF, termos e PDDE." },
  { icon: Wrench, title: "Peça novos serviços", desc: "Solicite manutenção num toque." },
  { icon: CalendarClock, title: "Alertas de validade", desc: "Avisos antes do vencimento." },
  { icon: Bell, title: "Notificações no celular", desc: "Instale o app e receba em tempo real." },
];

export default function WelcomeInviteDialog({ open, onOpenChange, schoolName }) {
  const [fdpOpen, setFdpOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FdpInfoDialog open={fdpOpen} onOpenChange={setFdpOpen} />
      <DialogContent
        className="w-[94vw] max-w-md p-0 overflow-hidden gap-0 max-h-[88vh] grid grid-rows-[auto_1fr] [&>button]:hidden"
        data-testid="welcome-invite-dialog"
      >
        {/* Sticky header with always-visible close */}
        <div className="relative bg-gradient-to-br from-[#1e3a8a] to-[#7c3aed] px-4 pt-4 pb-4 text-white">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Fechar"
            data-testid="welcome-invite-close"
            className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-white/25 hover:bg-white/40 active:bg-white/50 backdrop-blur flex items-center justify-center transition-colors z-10"
          >
            <X className="w-5 h-5 text-white" strokeWidth={2.6} />
          </button>
          <div className="flex items-center gap-2 mb-2 pr-10">
            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
              <SchoolIcon className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur">
              <Sparkles className="w-3 h-3" />
              <span className="text-[9px] font-bold uppercase tracking-widest">
                Novo sistema
              </span>
            </div>
          </div>
          <DialogTitle className="text-lg font-black text-white leading-tight">
            Bem-vindo(a) ao Portal AMC Escolas!
          </DialogTitle>
          <DialogDescription className="text-[12px] text-white/90 mt-1 leading-snug">
            {schoolName
              ? `Novo acesso digital da ${schoolName}.`
              : "Novo acesso digital da sua escola."}{" "}
            Acompanhe tudo da manutenção em um só lugar.
          </DialogDescription>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-4 py-4 bg-white">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2.5">
            O que você vai encontrar
          </p>
          <ul className="space-y-2.5">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex gap-2.5">
                <div className="w-8 h-8 flex-shrink-0 rounded-lg bg-[#f5f3ff] flex items-center justify-center">
                  <f.icon className="w-4 h-4 text-[#7c3aed]" strokeWidth={2.2} />
                </div>
                <div className="min-w-0 pt-0.5">
                  <p className="text-[13px] font-semibold text-slate-900 leading-tight">
                    {f.title}
                  </p>
                  <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <p className="text-[11px] text-amber-900 leading-snug">
              <strong>Importante:</strong> sistema <strong>novo</strong>. Mesmo quem já era
              cliente precisa criar o acesso aqui pela primeira vez.
            </p>
          </div>

          {/* Chamativo CTA → explicar sistema FDP */}
          <button
            type="button"
            onClick={() => setFdpOpen(true)}
            data-testid="welcome-invite-open-fdp"
            className="group w-full mt-3 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl p-3 flex items-center gap-3 shadow-lg shadow-emerald-500/30 active:scale-[0.98] transition-all"
          >
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
              <Handshake className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-50 leading-none">
                Diretor(a), você conhece?
              </p>
              <p className="text-[13px] font-black text-white leading-tight mt-1">
                Sistema Faz Depois Paga
              </p>
              <p className="text-[11px] text-emerald-50 leading-snug mt-0.5">
                Clica aqui e vem conferir como funciona
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-white shrink-0 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.4} />
          </button>

          <Button
            onClick={() => onOpenChange(false)}
            className="w-full h-11 mt-3 bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl font-semibold text-sm"
            data-testid="welcome-invite-continue"
          >
            Continuar e criar meu acesso
          </Button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-full mt-1.5 h-8 text-[11px] font-semibold text-slate-500 hover:text-slate-700"
            data-testid="welcome-invite-dismiss"
          >
            Fechar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
